import { Injectable, NgZone } from '@angular/core';
import { from, interval, of, switchMap, tap, catchError, map, Observable } from 'rxjs';
import { MicrophoneStore } from '../stores/microphone.store';
import { AudioInputDevice } from '../../models/app/precall/audio-input-device';

@Injectable({
  providedIn: 'root',
})
export class MicrophoneService {
  private analyser?: AnalyserNode;
  private audioContext?: AudioContext;
  private sourceNode?: MediaStreamAudioSourceNode;
  private stream?: MediaStream;
  private levelPolling$ = interval(100); // emit audio level every 100ms

  constructor(
    public store: MicrophoneStore,
    private zone: NgZone,
  ) {}

  /**
   * Requests permission to access the microphone and initializes audio stream.
   * Also starts monitoring the audio level and lists available devices.
   */
  requestPermission$() {
    return from(navigator.mediaDevices.getUserMedia({ audio: true })).pipe(
      tap((stream) => {
        this.stream = stream;
        this.store.updateState({ permissionGranted: true, streamActive: true });
      }),
      tap(() => this.monitorAudioLevel(this.stream!)),
      switchMap(() => this.refreshDevices()),
      catchError((err) => {
        this.store.updateState({
          permissionGranted: false,
          streamActive: false,
          error: err.message,
        });
        return of(null);
      }),
    );
  }

  /**
   * Lists all available audio input devices and updates the store.
   */
  refreshDevices() {
    return from(navigator.mediaDevices.enumerateDevices()).pipe(
      map((devices) => devices.filter((d) => d.kind === 'audioinput')),
      map((inputs): AudioInputDevice[] =>
        inputs.map((d) => ({
          deviceId: d.deviceId,
          label: d.label,
          groupId: d.groupId,
        })),
      ),
      tap((devices) => this.store.updateDevices(devices)),
    );
  }

  /**
   * Switches to a different audio input device by deviceId.
   * @param deviceId The deviceId of the selected audio input.
   */
  switchDevice$(deviceId: string) {
    return this.stop$().pipe(
      switchMap(() =>
        from(
          navigator.mediaDevices.getUserMedia({
            audio: { deviceId: { exact: deviceId } },
          }),
        ),
      ),
      tap((stream) => {
        this.stream = stream;
        this.store.updateState({ streamActive: true, activeDeviceId: deviceId });
        this.monitorAudioLevel(stream);
      }),
      catchError((err) => {
        this.store.updateState({ error: err.message });
        return of(null);
      }),
    );
  }

  /**
   * Stops the current audio stream and cleans up audio processing.
   */
  stop$() {
    return of(null).pipe(
      tap(() => {
        this.analyser?.disconnect();
        this.sourceNode?.disconnect();
        this.audioContext?.close();
        this.stream?.getTracks().forEach((t) => t.stop());

        this.store.updateState({
          streamActive: false,
          audioLevel: 0,
          activeDeviceId: undefined,
        });
      }),
    );
  }

  /**
   * Starts real-time monitoring of the microphone audio level (RMS normalized).
   * @param stream The MediaStream from the selected audio input.
   */
  private monitorAudioLevel(stream: MediaStream): void {
    this.audioContext = new AudioContext();
    this.sourceNode = this.audioContext.createMediaStreamSource(stream);
    this.analyser = this.audioContext.createAnalyser();

    this.sourceNode.connect(this.analyser);
    this.analyser.fftSize = 256;

    const dataArray = new Uint8Array(this.analyser.frequencyBinCount);

    this.zone.runOutsideAngular(() => {
      this.levelPolling$
        .pipe(
          tap(() => {
            this.analyser!.getByteTimeDomainData(dataArray);
            const rms = Math.sqrt(
              dataArray.reduce((sum, val) => sum + Math.pow(val - 128, 2), 0) / dataArray.length,
            );
            const level = Math.min(rms / 128, 1);
            this.store.updateState({ audioLevel: level });
          }),
        )
        .subscribe();
    });
  }

  onPermissionChange(): Observable<PermissionState> {
    if (!navigator.permissions || !navigator.permissions.query) {
      console.warn('Permissions API not supported in this browser.');
      return of('prompt'); // fallback
    }

    return new Observable<PermissionState>((subscriber) => {
      navigator.permissions
        .query({ name: 'microphone' as PermissionName })
        .then((status) => {
          // Emit initial state
          subscriber.next(status.state);

          // Emit on every change
          const handler = () => subscriber.next(status.state);
          status.addEventListener('change', handler);

          // Cleanup on unsubscribe
          return () => {
            status.removeEventListener('change', handler);
          };
        })
        .catch((err) => {
          console.error('Error querying microphone permission:', err);
          subscriber.error(err);
        });
    });
  }
}
